import React, { FC, useCallback, useContext, useState } from "react";
import { ModalContext } from "contexts/modals";
import VKButton from "components/vkButton";
import { LocalizeText } from "components/localizer";
import { TFormEvent } from "utils/types";
import { ListItemText, List, ListItem, Box, Grid } from "@mui/material";

import { IMembership } from "reducers/memberships";
import ConfirmDialog from "components/confirmDialog";
import { dateIsPastOrToday } from "utils/helpers";
import { parseISO } from "date-fns";

interface IProps {
    membership: IMembership;
    onAcceptTerminate: (membershipId: number) => Promise<void>;
    onAcceptReactivate: (membershipId: number) => Promise<void>;
}

const EditStatusMembershipDrawer: FC<IProps> = ({ membership, onAcceptTerminate, onAcceptReactivate }) => {
    const [showConfirm, setShowConfirm] = useState<boolean>(false);
    const { closeDrawers } = useContext(ModalContext);

    const handleSubmit = (event: TFormEvent): void => {
        setShowConfirm(true);
    };

    const onInactivate = useCallback(async () => {
        await onAcceptTerminate(membership.id);
        closeDrawers();
    }, [closeDrawers, membership.id, onAcceptTerminate]);

    const onReactivate = useCallback(async () => {
        await onAcceptReactivate(membership.id);
        closeDrawers();
    }, [closeDrawers, membership.id, onAcceptReactivate]);

    if (dateIsPastOrToday(parseISO(membership.end_date ?? ""))) {
        return (
            <Box>
                <form onSubmit={handleSubmit}>
                    <List>
                        <ListItem>
                            <ListItemText primary={<LocalizeText tag="reactivateMembershipText" />} />
                        </ListItem>
                        <ListItem>
                            <Grid container justifyContent="space-between" marginTop="40px">
                                <VKButton type="submit" tag="reactivateNow" template="primary" />
                                <VKButton tag="cancel" template="cancel" onClick={closeDrawers} />
                            </Grid>
                        </ListItem>
                    </List>
                    <ConfirmDialog
                        open={showConfirm}
                        title={<LocalizeText tag="reactivateTheMembership" />}
                        description={<LocalizeText tag={"reactivateMembershipSure"} />}
                        onReject={() => setShowConfirm(false)}
                        onAccept={onReactivate}
                    />
                </form>
            </Box>
        );
    } else {
        return (
            <Box>
                <form onSubmit={handleSubmit}>
                    <List>
                        <ListItem>
                            <ListItemText primary={<LocalizeText tag="inactivateMembershipText" />} />
                        </ListItem>
                        <ListItem>
                            <Grid container justifyContent="space-between" marginTop="40px">
                                <VKButton type="submit" tag="deactivateNow" template="primary" />
                                <VKButton tag="cancel" template="cancel" onClick={closeDrawers} />
                            </Grid>
                        </ListItem>
                    </List>
                    <ConfirmDialog
                        open={showConfirm}
                        title={<LocalizeText tag="inactivateTheMembership" />}
                        description={<LocalizeText tag={"inactivateMembershipSure"} />}
                        onReject={() => setShowConfirm(false)}
                        onAccept={onInactivate}
                    />
                </form>
            </Box>
        );
    }
};

export default EditStatusMembershipDrawer;
